import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import clsx from "clsx";
import { ReactNode } from "react";
import { useRadioOptionSelectionStyles } from "./radio-option-selection.styles";

export type RadioOptionItem<T extends string = string> = {
  label: string;
  value: T;
};

export type RadioOptionSelectionProps<T extends string = string> = {
  options: readonly RadioOptionItem<T>[];
  value: T | "";
  onChange: (value: T) => void;
  otherValue?: string;
  onOtherValueChange?: (value: string) => void;
  otherOptionValue?: T;
  note?: ReactNode;
  otherFieldLabel?: string;
  otherFieldPlaceholder?: string;
  otherFieldId?: string;
  otherFieldRequired?: boolean;
};

export function RadioOptionSelection<T extends string = string>({
  options,
  value,
  onChange,
  otherValue = "",
  onOtherValueChange,
  otherOptionValue = "other" as T,
  note,
  otherFieldLabel = "Please specify",
  otherFieldPlaceholder = "Enter details",
  otherFieldId = "option-selection-other",
  otherFieldRequired = true,
}: RadioOptionSelectionProps<T>) {
  const classes = useRadioOptionSelectionStyles();
  const showOtherField = value === otherOptionValue && onOtherValueChange;

  return (
    <Stack gap={2}>
      {note}

      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          value={value}
          onChange={(event) => onChange(event.target.value as T)}
        >
          <Stack gap={1}>
            {options.map((option) => {
              const isSelected = value === option.value;

              return (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  className={clsx(classes.option, {
                    [classes.optionSelected]: isSelected,
                  })}
                  control={<Radio size="small" color="primary" />}
                  label={
                    <Typography
                      variant="body2"
                      fontWeight={isSelected ? 600 : 400}
                    >
                      {option.label}
                    </Typography>
                  }
                />
              );
            })}
          </Stack>
        </RadioGroup>
      </FormControl>

      {showOtherField && (
        <Stack gap={1}>
          <Typography variant="body2" fontWeight={600}>
            {otherFieldLabel}
            {otherFieldRequired && (
              <Typography component="span" color="error">
                {" "}
                *
              </Typography>
            )}
          </Typography>
          <TextField
            id={otherFieldId}
            value={otherValue}
            onChange={(event) => onOtherValueChange(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={3}
            placeholder={otherFieldPlaceholder}
            className={classes.otherField}
          />
        </Stack>
      )}
    </Stack>
  );
}
